import { memo } from 'react';

import { type OpenCreditPackModalLocations } from '@air/analytics';
import { ModalDescription } from '@air/primitive-modal';

import { CreditPackLineItem } from '~/components/Modals/ManageCreditPacks/CreditPackLineItem';
import { CreditPackModalWrapper } from '~/components/Modals/ManageCreditPacks/CreditPackModalWrapper';
import { UpdateCreditPacksForm } from '~/components/Modals/ManageCreditPacks/UpdateCreditPacksForm';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { formatMonthlyCredits } from '~/utils/creditUtils';
import { getSubscriptionInfo } from '~/utils/SubscriptionUtils';

export type UpdateCreditPacksModalProps = AirModalProps<{
  /** Pre-select this quantity instead of the subscription's current pack count. */
  initialSelectedQuantity?: number;
  /** Surface that opened the manage credit packs flow. Pass `undefined` explicitly when there is
   *  no attributable surface. */
  source: OpenCreditPackModalLocations | undefined;
  /** Optional override for the upgrade branch. When provided, replaces the default navigation-to-checkout. */
  onUpgradeConfirmed?: (quantity: number) => void;
}>;

export const UpdateCreditPacksModal = memo(
  ({ initialSelectedQuantity, source, onUpgradeConfirmed, onClose }: UpdateCreditPacksModalProps) => {
    const { data: subscription } = useSubscriptionContext();
    const { data: subscriptionPlan } = useSubscriptionPlan();

    const subscriptionInfo = getSubscriptionInfo({
      subscription,
      plan: subscriptionPlan?.plan,
      totalBillableSeats: 0,
    });

    const title =
      subscriptionInfo?.creditPack && subscriptionInfo.creditPack.quantity > 0
        ? 'Manage monthly credits'
        : 'Increase monthly credits';

    if (!subscription || !subscriptionInfo?.creditPack || !subscriptionInfo.billingCycle) {
      return (
        <CreditPackModalWrapper
          title={title}
          isLoading
          dangerouslyBypassFocusLock
          onClose={onClose}
          testId="UPDATE_CREDIT_PACKS_MODAL"
        />
      );
    }

    const {
      billingCycle,
      planPriceCents,
      planCredits,
      creditPack,
      totalPriceCents: currentTotalPriceCents,
    } = subscriptionInfo;

    const initialSelection = initialSelectedQuantity ?? (creditPack.quantity > 0 ? creditPack.quantity : 1);

    return (
      <CreditPackModalWrapper
        title={title}
        dangerouslyBypassFocusLock
        onClose={onClose}
        testId="UPDATE_CREDIT_PACKS_MODAL"
      >
        <ModalDescription className="ads:text-metadata text-14 text-gray-10">
          Grow your plan to the right size with monthly credit packs to expand your storage capabilities and AI-powered
          generations and iterations.
        </ModalDescription>

        <div>
          <p className="mb-2 font-medium ads:text-primary text-10 text-gray-12 uppercase tracking-wider">
            Current plan
          </p>
          <div className="flex flex-col gap-2 rounded-12 border ads:border-default border-gray-4 px-4 py-3">
            <CreditPackLineItem
              label={`${subscription.planName} Plan`}
              priceInCents={planPriceCents}
              billingCycle={billingCycle}
              description={formatMonthlyCredits(planCredits)}
            />

            {creditPack.quantity > 0 && (
              <>
                <CreditPackLineItem
                  label="Credit pack"
                  priceInCents={creditPack.totalPriceCents}
                  billingCycle={billingCycle}
                  description={formatMonthlyCredits(creditPack.credits)}
                />
                <CreditPackLineItem
                  label="Current price"
                  priceInCents={currentTotalPriceCents}
                  billingCycle={billingCycle}
                  separator
                />
              </>
            )}
          </div>
        </div>

        <UpdateCreditPacksForm
          initialSelectedQuantity={initialSelection}
          source={source}
          onUpgradeConfirmed={onUpgradeConfirmed}
          onClose={onClose}
        />
      </CreditPackModalWrapper>
    );
  },
);

UpdateCreditPacksModal.displayName = 'UpdateCreditPacksModal';
